import { background, black, divider, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { elevation, rounded } from "@/theme/variables";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

const OVERFLOW_BADGE_WIDTH = 32;
const TAG_GAP = 8;

export type ResolvedDocumentTag = {
  id: string | number;
  label: string;
  bgColor: string;
  color: string;
  hasBorder: boolean;
};

type Props = {
  tags: ResolvedDocumentTag[];
};

const TagBadge = ({ tag }: { tag: ResolvedDocumentTag }) => (
  <Box
    sx={{
      borderRadius: rounded.md,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: tag.bgColor,
      color: tag.color,
      border: tag.hasBorder ? `1px solid ${black[95]}` : "none",
      ...typography.cap12,
      lineHeight: 1,
      padding: "4px 10px",
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {tag.label}
  </Box>
);

export const DocumentTypeTagsReadOnly = ({ tags }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState(tags.length);

  useEffect(() => {
    const container = containerRef.current;
    const measureContainer = measureRef.current;
    if (!container || !measureContainer) return;

    const calculate = () => {
      const availableWidth = container.offsetWidth;
      if (availableWidth === 0 || tags.length === 0) {
        setMaxVisible(tags.length);
        return;
      }

      const tagEls = measureContainer.querySelectorAll("[data-measure-tag]");
      let usedWidth = 0;
      let fitCount = 0;

      for (let i = 0; i < tagEls.length; i++) {
        const tagWidth = (tagEls[i] as HTMLElement).offsetWidth + TAG_GAP;
        const hasMore = i < tags.length - 1;
        const extra = hasMore ? OVERFLOW_BADGE_WIDTH + TAG_GAP : 0;

        if (usedWidth + tagWidth + extra <= availableWidth) {
          usedWidth += tagWidth;
          fitCount++;
        } else {
          break;
        }
      }

      setMaxVisible(Math.max(fitCount, 1));
    };

    requestAnimationFrame(calculate);

    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [tags]);

  const visibleTags = tags.slice(0, maxVisible);
  const overflowTags = tags.slice(maxVisible);
  const overflowCount = overflowTags.length;

  if (tags.length === 0) return null;

  return (
    <>
      {/* Hidden measurement container — renders ALL tags to measure widths */}
      <Box
        ref={measureRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          gap: `${TAG_GAP}px`,
          whiteSpace: "nowrap",
        }}
      >
        {tags.map((tag) => (
          <Box key={tag.id} data-measure-tag sx={{ flexShrink: 0 }}>
            <TagBadge tag={tag} />
          </Box>
        ))}
      </Box>

      {/* Visible tags */}
      <Stack ref={containerRef} direction="row" alignItems="center" justifyContent="end" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        {visibleTags.map((tag) => (
          <TagBadge key={tag.id} tag={tag} />
        ))}
        {overflowCount > 0 && (
          <Tooltip
            arrow
            placement="top"
            title={
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {overflowTags.map((tag) => (
                  <TagBadge key={tag.id} tag={tag} />
                ))}
              </Stack>
            }
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: background.white,
                  color: text.primary,
                  boxShadow: elevation.lv2,
                  border: `1px solid ${divider.middle}`,
                  borderRadius: rounded.xs,
                  padding: "8px",
                  maxWidth: "none",
                },
              },
              arrow: {
                sx: { color: background.white, "&::before": { border: `1px solid ${divider.middle}` } },
              },
            }}
          >
            <Typography
              variant="body14"
              component="span"
              sx={{
                color: text.primary,
                textDecoration: "underline",
                cursor: "default",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              +{overflowCount}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </>
  );
};
